import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  Select,
  Tab,
  TabList,
  Textarea,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowDown16Regular,
  ArrowUp16Regular,
  Copy20Regular,
  Delete20Regular,
  Dismiss24Regular,
  Flash20Regular,
  Link20Regular,
} from "@fluentui/react-icons";
import styles from "./TaskManager.module.css";
import px from "./Taskextras.module.css";
import { api, crud, iso, msgOf, R } from "./Api/Taskapi";
import type { Person, Project } from "./Api/Taskapi";
import { Chip, FormDialog, Wait, fmtDate, fmtStamp, nice, useAct, useLoad } from "./Taskpages";
import type { Notify, PageCtx } from "./Taskpages";

/* ------------------------------------------------------------------ *
 *  Task extras: quick add, subtasks, dependencies, tags, followers,
 *  assignees, links, custom fields, milestones, activity, bulk edit,
 *  templates and project settings. All of them use the API as it is.
 * ------------------------------------------------------------------ */

const ROUTES = {
  follower: "tasks/tsk/taskfollower",
  tag: "tasks/tsk/tag",
  taskTag: "tasks/tsk/tasktag",
  dependency: "tasks/tsk/taskdependency",
  milestone: "tasks/tsk/milestone",
  attachment: "tasks/tsk/attachment",
  activity: "tasks/tsk/taskactivity",
  template: "tasks/tsk/tasktemplate",
  fieldDef: "workspaces/prj/customfielddef",
  fieldValue: "tasks/tsk/customfieldvalue",
  projectMember: "workspaces/prj/projectmember",
  favorite: "workspaces/prj/favorite",
};

type Row = Record<string, unknown>;
const num = (v: unknown) => Number(v);
const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));

/* =================================================================== *
 *  Quick add – type a sentence, the server finds the date, priority,
 *  tags (#tag) and people (@name).
 * =================================================================== */
interface Parsed {
  title: string;
  priority?: string | null;
  dueDate?: string | null;
  dueTime?: string | null;
  recurrence?: string | null;
  tags?: string[] | null;
  mentions?: string[] | null;
}

export const QuickAddBar: React.FC<{ ctx: PageCtx; onAdded: () => void }> = ({ ctx, onAdded }) => {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const t = text.trim();
    if (t.length < 3) {
      setParsed(null);
      return;
    }
    const mine = ++seq.current;
    const h = window.setTimeout(() => {
      api<{ parsed: Parsed }>("POST", "/api/tasks/quick-add", { text: t, workspaceId: ctx.workspaceId, projectId: ctx.projectId, preview: true })
        .then((r) => mine === seq.current && setParsed(r.parsed))
        .catch(() => mine === seq.current && setParsed(null));
    }, 350);
    return () => window.clearTimeout(h);
  }, [text, ctx.workspaceId, ctx.projectId]);

  const add = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api("POST", "/api/tasks/quick-add", { text: text.trim(), workspaceId: ctx.workspaceId, projectId: ctx.projectId });
      setText("");
      setParsed(null);
      ctx.notify("success", "Task added.");
      onAdded();
    } catch (e) {
      ctx.notify("error", msgOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={px.quick}>
      <Input
        className={px.grow}
        value={text}
        placeholder="Quick add: “Call supplier tomorrow 3pm !high #finance @amaka”"
        aria-label="Quick add a task"
        contentBefore={<Flash20Regular />}
        onChange={(_, d) => setText(d.value)}
        onKeyDown={(e) => e.key === "Enter" && void add()}
      />
      <Button className={styles.primary} appearance="primary" disabled={!text.trim() || busy} onClick={() => void add()}>
        Add
      </Button>
      {parsed && (
        <div className={px.parsed} aria-live="polite">
          <span className={px.parsedTitle}>{parsed.title}</span>
          {parsed.dueDate && <Chip text={`Due ${fmtDate(parsed.dueDate)}${parsed.dueTime ? ` ${parsed.dueTime.slice(0, 5)}` : ""}`} />}
          {parsed.priority && parsed.priority !== "NONE" && <Chip text={parsed.priority} />}
          {parsed.recurrence && <Chip text={`Repeats ${parsed.recurrence}`} />}
          {(parsed.tags ?? []).map((t) => (
            <Chip key={t} text={`#${t}`} />
          ))}
          {(parsed.mentions ?? []).map((t) => (
            <Chip key={t} text={`@${t}`} />
          ))}
        </div>
      )}
    </div>
  );
};

/* =================================================================== *
 *  One task: sections shown as tabs in the detail window
 * =================================================================== */
interface TaskRow {
  id: number;
  workspaceId: number;
  projectId?: number | null;
  title: string;
  startDate?: string | null;
  estimateMinutes?: number | null;
  storyPoints?: number | null;
  recurrenceRule?: string | null;
  isCompleted: boolean;
  isUrgent: boolean;
  isImportant: boolean;
  archivedAt?: string | null;
}
interface SubRow {
  id: number;
  depth: number;
  title: string;
  isCompleted: boolean;
  dueDate?: string | null;
}
interface Subtasks {
  items: SubRow[];
  total: number;
  done: number;
  percentComplete: number;
}
interface Blockers {
  blocked: boolean;
  items: { id: number; title: string; dependencyType: string; isCompleted: boolean; dueDate?: string | null }[];
}

const PlanSection: React.FC<{ taskId: number; ctx: PageCtx; onChanged: () => void; onOpen: (id: number) => void }> = ({ taskId, ctx, onChanged, onOpen }) => {
  const act = useAct(ctx.notify);
  const task = useLoad(() => crud.get<TaskRow>(R.task, taskId), [taskId]);
  const subs = useLoad(() => api<Subtasks>("GET", `/api/tasks/${taskId}/subtasks`), [taskId]);
  const blockers = useLoad(() => api<Blockers>("GET", `/api/tasks/${taskId}/blockers`), [taskId]);
  const deps = useLoad(async () => (await crud.list<Row>(ROUTES.dependency, { successorTaskId: taskId })).items, [taskId]);
  const t = task.data;
  const [f, setF] = useState({ start: "", est: "", points: "", rrule: "" });
  const [dates, setDates] = useState<string[]>([]);
  const [subTitle, setSubTitle] = useState("");
  const [pre, setPre] = useState({ q: "", id: "", type: "FS", lag: "0" });
  const found = useLoad(
    async () => (pre.q.trim().length >= 2 && t?.projectId ? (await crud.list<{ id: number; title: string }>(R.task, { projectId: t.projectId, q: pre.q.trim(), pageSize: 20 })).items.filter((x) => x.id !== taskId) : []),
    [pre.q, t?.projectId, taskId],
  );

  useEffect(() => {
    if (t) setF({ start: t.startDate?.slice(0, 10) ?? "", est: t.estimateMinutes ? String(t.estimateMinutes) : "", points: t.storyPoints ? String(t.storyPoints) : "", rrule: t.recurrenceRule ?? "" });
  }, [t]);

  const refresh = () => {
    task.reload();
    subs.reload();
    blockers.reload();
    deps.reload();
    onChanged();
  };
  const titleOf = (id: number) => blockers.data?.items.find((b) => b.id === id)?.title ?? `Task ${id}`;

  const saveFields = async () => {
    if (await act(() => crud.update(R.task, taskId, { startDate: f.start || null, estimateMinutes: f.est ? Number(f.est) : null, storyPoints: f.points ? Number(f.points) : null, recurrenceRule: f.rrule.trim() || null }), "Saved.")) refresh();
  };

  return (
    <div className={px.section}>
      <Wait loading={task.loading && !t} error={task.error} onRetry={task.reload} />
      {t && (
        <>
          <div className={px.grid}>
            <Field label="Start date">
              <Input type="date" value={f.start} onChange={(_, d) => setF({ ...f, start: d.value })} />
            </Field>
            <Field label="Estimate (minutes)">
              <Input type="number" min="0" value={f.est} onChange={(_, d) => setF({ ...f, est: d.value })} />
            </Field>
            <Field label="Story points">
              <Input type="number" min="0" step="0.5" value={f.points} onChange={(_, d) => setF({ ...f, points: d.value })} />
            </Field>
            <Field label="Repeats (RRULE)" hint="For example FREQ=WEEKLY;BYDAY=MO">
              <Input value={f.rrule} onChange={(_, d) => setF({ ...f, rrule: d.value })} />
            </Field>
          </div>
          <div className={px.actions}>
            <Button className={styles.primary} appearance="primary" onClick={() => void saveFields()}>
              Save
            </Button>
            <Button
              disabled={!f.rrule.trim()}
              onClick={async () => {
                try {
                  const r = await api<{ dates: string[] }>("POST", "/api/automation/recurrence/preview", { rrule: f.rrule.trim(), count: 6 });
                  setDates(r.dates ?? []);
                } catch (e) {
                  ctx.notify("error", msgOf(e));
                  setDates([]);
                }
              }}
            >
              Preview dates
            </Button>
            {dates.length > 0 && <span className={styles.muted}>{dates.map((d) => fmtDate(d)).join(" · ")}</span>}
          </div>
        </>
      )}

      <div className={px.block}>
        <div className={px.blockTitle}>
          Subtasks {subs.data && subs.data.total > 0 ? `(${subs.data.done}/${subs.data.total} · ${Math.round(subs.data.percentComplete)}%)` : ""}
        </div>
        {(subs.data?.items ?? [])
          .filter((s) => s.depth > 0)
          .map((s) => (
            <div key={s.id} className={px.line} style={{ paddingLeft: (s.depth - 1) * 18 }}>
              <Checkbox
                checked={s.isCompleted}
                aria-label={`Done: ${s.title}`}
                onChange={async (_, d) => {
                  if (await act(() => api("POST", `/api/tasks/${s.id}/${d.checked === true ? "complete" : "reopen"}`))) refresh();
                }}
              />
              <button type="button" className={px.linkBtn} onClick={() => onOpen(s.id)}>
                {s.title}
              </button>
              {s.dueDate && <span className={styles.muted}>{fmtDate(s.dueDate)}</span>}
            </div>
          ))}
        <div className={px.addRow}>
          <Input className={px.grow} placeholder="Add a subtask" value={subTitle} onChange={(_, d) => setSubTitle(d.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />
          <Button
            icon={<Add20Regular />}
            disabled={!subTitle.trim() || !t}
            onClick={async () => {
              if (!t) return;
              if (await act(() => crud.create(R.task, { workspaceId: t.workspaceId, projectId: t.projectId ?? null, parentTaskId: taskId, title: subTitle.trim() }))) {
                setSubTitle("");
                refresh();
              }
            }}
          >
            Add
          </Button>
        </div>
      </div>

      <div className={px.block}>
        <div className={px.blockTitle}>
          Waiting on {blockers.data?.blocked ? <Chip text="Blocked" /> : null}
        </div>
        {(deps.data ?? []).map((d) => (
          <div key={num(d.id)} className={px.line}>
            <button type="button" className={px.linkBtn} onClick={() => onOpen(num(d.predecessorTaskId))}>
              {titleOf(num(d.predecessorTaskId))}
            </button>
            <Chip text={str(d.dependencyType)} />
            {num(d.lagDays) !== 0 && <span className={styles.muted}>{num(d.lagDays)} d lag</span>}
            <Button
              size="small"
              appearance="subtle"
              icon={<Delete20Regular />}
              aria-label="Remove dependency"
              onClick={async () => {
                if (await act(() => crud.remove(ROUTES.dependency, num(d.id)))) refresh();
              }}
            />
          </div>
        ))}
        {(deps.data ?? []).length === 0 && <span className={styles.muted}>Nothing blocks this task.</span>}
        <div className={px.addRow}>
          <Input className={px.grow} placeholder="Find a task in this project" value={pre.q} onChange={(_, d) => setPre({ ...pre, q: d.value, id: "" })} />
          <Select aria-label="Task found" value={pre.id} onChange={(_, d) => setPre({ ...pre, id: d.value })}>
            <option value="">{pre.q.trim().length < 2 ? "Type to search" : "Choose a task"}</option>
            {(found.data ?? []).map((x) => (
              <option key={x.id} value={String(x.id)}>
                {x.title}
              </option>
            ))}
          </Select>
          <Select aria-label="Type" value={pre.type} onChange={(_, d) => setPre({ ...pre, type: d.value })}>
            <option value="FS">Finish → Start</option>
            <option value="SS">Start → Start</option>
            <option value="FF">Finish → Finish</option>
            <option value="SF">Start → Finish</option>
          </Select>
          <Input type="number" aria-label="Lag in days" className={px.small} value={pre.lag} onChange={(_, d) => setPre({ ...pre, lag: d.value })} />
          <Button
            icon={<Link20Regular />}
            disabled={!pre.id || !t}
            onClick={async () => {
              if (!t) return;
              if (await act(() => crud.create(ROUTES.dependency, { workspaceId: t.workspaceId, predecessorTaskId: Number(pre.id), successorTaskId: taskId, dependencyType: pre.type, lagDays: Number(pre.lag) || 0 }), "Linked.")) {
                setPre({ q: "", id: "", type: "FS", lag: "0" });
                refresh();
              }
            }}
          >
            Link
          </Button>
        </div>
      </div>
    </div>
  );
};

const PeopleSection: React.FC<{ taskId: number; workspaceId: number; ctx: PageCtx; onChanged: () => void }> = ({ taskId, workspaceId, ctx, onChanged }) => {
  const act = useAct(ctx.notify);
  const assignees = useLoad(async () => (await crud.list<Row>(R.assignee, { taskId })).items, [taskId]);
  const followers = useLoad(async () => (await crud.list<Row>(ROUTES.follower, { taskId })).items, [taskId]);
  const taskTags = useLoad(async () => (await crud.list<Row>(ROUTES.taskTag, { taskId })).items, [taskId]);
  const tags = useLoad(async () => (await crud.list<{ id: number; name: string; color?: string | null }>(ROUTES.tag, { workspaceId, sort: "name" })).items, [workspaceId]);
  const [pickA, setPickA] = useState("");
  const [pickF, setPickF] = useState("");
  const [pickT, setPickT] = useState("");
  const [newTag, setNewTag] = useState("");
  const nameOf = (id: number) => ctx.people.find((p) => p.id === id)?.name ?? `User ${id}`;
  const tagName = (id: number) => tags.data?.find((t) => t.id === id)?.name ?? `Tag ${id}`;
  const refresh = () => {
    assignees.reload();
    followers.reload();
    taskTags.reload();
    tags.reload();
    onChanged();
  };
  const personSelect = (value: string, set: (v: string) => void, taken: number[], label: string) => (
    <Select aria-label={label} value={value} onChange={(_, d) => set(d.value)}>
      <option value="">{label}</option>
      {ctx.people
        .filter((p: Person) => !taken.includes(p.id))
        .map((p) => (
          <option key={p.id} value={String(p.id)}>
            {p.name}
          </option>
        ))}
    </Select>
  );
  const aIds = (assignees.data ?? []).map((a) => num(a.userId));
  const fIds = (followers.data ?? []).map((a) => num(a.userId));
  const tIds = (taskTags.data ?? []).map((a) => num(a.tagId));

  return (
    <div className={px.section}>
      <div className={px.block}>
        <div className={px.blockTitle}>Assigned to</div>
        {(assignees.data ?? []).map((a) => (
          <div key={num(a.id)} className={px.line}>
            <span>{nameOf(num(a.userId))}</span>
            {a.isPrimary ? (
              <Chip text="Lead" />
            ) : (
              <Button
                size="small"
                onClick={async () => {
                  if (await act(() => crud.update(R.assignee, num(a.id), { isPrimary: true }))) refresh();
                }}
              >
                Make lead
              </Button>
            )}
            <Button
              size="small"
              appearance="subtle"
              icon={<Delete20Regular />}
              aria-label={`Unassign ${nameOf(num(a.userId))}`}
              onClick={async () => {
                if (await act(() => crud.remove(R.assignee, num(a.id)))) refresh();
              }}
            />
          </div>
        ))}
        <div className={px.addRow}>
          {personSelect(pickA, setPickA, aIds, "Add a person")}
          <Button
            icon={<Add20Regular />}
            disabled={!pickA}
            onClick={async () => {
              if (await act(() => crud.create(R.assignee, { workspaceId, taskId, userId: Number(pickA) }))) {
                setPickA("");
                refresh();
              }
            }}
          >
            Assign
          </Button>
        </div>
      </div>

      <div className={px.block}>
        <div className={px.blockTitle}>Following</div>
        {(followers.data ?? []).map((a) => (
          <div key={num(a.id)} className={px.line}>
            <span>{nameOf(num(a.userId))}</span>
            <Button
              size="small"
              appearance="subtle"
              icon={<Delete20Regular />}
              aria-label={`Stop ${nameOf(num(a.userId))} following`}
              onClick={async () => {
                if (await act(() => crud.remove(ROUTES.follower, num(a.id)))) refresh();
              }}
            />
          </div>
        ))}
        <div className={px.addRow}>
          {personSelect(pickF, setPickF, fIds, "Add a follower")}
          <Button
            disabled={!pickF}
            onClick={async () => {
              if (await act(() => crud.create(ROUTES.follower, { workspaceId, taskId, userId: Number(pickF) }))) {
                setPickF("");
                refresh();
              }
            }}
          >
            Add
          </Button>
          {!fIds.includes(ctx.me.id) && (
            <Button
              onClick={async () => {
                if (await act(() => crud.create(ROUTES.follower, { workspaceId, taskId, userId: ctx.me.id }), "You follow this task.")) refresh();
              }}
            >
              Follow
            </Button>
          )}
        </div>
      </div>

      <div className={px.block}>
        <div className={px.blockTitle}>Tags</div>
        <div className={px.chips}>
          {(taskTags.data ?? []).map((tt) => (
            <span key={num(tt.id)} className={px.tag}>
              {tagName(num(tt.tagId))}
              <button
                type="button"
                className={px.tagX}
                aria-label={`Remove tag ${tagName(num(tt.tagId))}`}
                onClick={async () => {
                  if (await act(() => crud.remove(ROUTES.taskTag, num(tt.id)))) refresh();
                }}
              >
                ×
              </button>
            </span>
          ))}
          {(taskTags.data ?? []).length === 0 && <span className={styles.muted}>No tags</span>}
        </div>
        <div className={px.addRow}>
          <Select aria-label="Existing tag" value={pickT} onChange={(_, d) => setPickT(d.value)}>
            <option value="">Add an existing tag</option>
            {(tags.data ?? [])
              .filter((t) => !tIds.includes(t.id))
              .map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
          </Select>
          <Button
            disabled={!pickT}
            onClick={async () => {
              if (await act(() => crud.create(ROUTES.taskTag, { workspaceId, taskId, tagId: Number(pickT) }))) {
                setPickT("");
                refresh();
              }
            }}
          >
            Add
          </Button>
          <Input className={px.grow} placeholder="or create a new tag" value={newTag} onChange={(_, d) => setNewTag(d.value)} />
          <Button
            disabled={!newTag.trim()}
            onClick={async () => {
              const name = newTag.trim();
              const existing = tags.data?.find((t) => t.name.toLowerCase() === name.toLowerCase());
              const ok = await act(async () => {
                const id = existing ? existing.id : (await crud.create<{ id: number }>(ROUTES.tag, { workspaceId, name })).id;
                if (!tIds.includes(id)) await crud.create(ROUTES.taskTag, { workspaceId, taskId, tagId: id });
              });
              if (ok) {
                setNewTag("");
                refresh();
              }
            }}
          >
            Create
          </Button>
        </div>
      </div>
    </div>
  );
};

interface FieldDef {
  id: number;
  name: string;
  fieldType: string;
  optionsJson?: string | null;
  isRequired: boolean;
}

const MoreSection: React.FC<{ taskId: number; workspaceId: number; projectId: number | null; ctx: PageCtx; onChanged: () => void }> = ({ taskId, workspaceId, projectId, ctx, onChanged }) => {
  const act = useAct(ctx.notify);
  const files = useLoad(async () => (await crud.list<Row>(ROUTES.attachment, { taskId })).items, [taskId]);
  const defs = useLoad(async () => (projectId === null ? [] : (await crud.list<FieldDef>(ROUTES.fieldDef, { projectId, sort: "sortOrder" })).items), [projectId]);
  const values = useLoad(async () => (await crud.list<Row>(ROUTES.fieldValue, { taskId })).items, [taskId]);
  const miles = useLoad(async () => (projectId === null ? [] : (await crud.list<Row>(ROUTES.milestone, { projectId, sort: "dueDate" })).items), [projectId]);
  const [link, setLink] = useState({ name: "", url: "" });
  const [ms, setMs] = useState({ name: "", due: "" });
  const [draft, setDraft] = useState<Record<number, string>>({});

  const valueOf = (defId: number) => values.data?.find((v) => num(v.fieldDefId) === defId);
  const shownValue = (d: FieldDef) => {
    if (draft[d.id] !== undefined) return draft[d.id] as string;
    const v = valueOf(d.id);
    if (!v) return "";
    return d.fieldType === "NUMBER" ? str(v.valueNumber) : d.fieldType === "DATE" ? str(v.valueDate).slice(0, 10) : str(v.valueText);
  };
  const saveValue = async (d: FieldDef) => {
    const text = (draft[d.id] ?? shownValue(d)).trim();
    const body: Record<string, unknown> =
      d.fieldType === "NUMBER" ? { valueNumber: text === "" ? null : Number(text) } : d.fieldType === "DATE" ? { valueDate: text || null } : { valueText: text || null };
    const cur = valueOf(d.id);
    const ok = await act(() => (cur ? crud.update(ROUTES.fieldValue, num(cur.id), body) : crud.create(ROUTES.fieldValue, { workspaceId, taskId, fieldDefId: d.id, ...body })), "Saved.");
    if (ok) {
      setDraft((o) => {
        const { [d.id]: _gone, ...rest } = o;
        return rest;
      });
      values.reload();
      onChanged();
    }
  };

  return (
    <div className={px.section}>
      <div className={px.block}>
        <div className={px.blockTitle}>Links and files</div>
        <p className={styles.muted}>Files can’t be uploaded yet, so attach a link (a shared drive, a design, a document).</p>
        {(files.data ?? []).map((a) => (
          <div key={num(a.id)} className={px.line}>
            <a className={px.linkBtn} href={str(a.storageUrl)} target="_blank" rel="noreferrer">
              {str(a.fileName)}
            </a>
            <Button
              size="small"
              appearance="subtle"
              icon={<Delete20Regular />}
              aria-label={`Remove ${str(a.fileName)}`}
              onClick={async () => {
                if (await act(() => crud.remove(ROUTES.attachment, num(a.id)))) {
                  files.reload();
                  onChanged();
                }
              }}
            />
          </div>
        ))}
        <div className={px.addRow}>
          <Input placeholder="Name" value={link.name} onChange={(_, d) => setLink({ ...link, name: d.value })} />
          <Input className={px.grow} placeholder="https://…" value={link.url} onChange={(_, d) => setLink({ ...link, url: d.value })} />
          <Button
            icon={<Add20Regular />}
            disabled={!link.name.trim() || !/^https?:\/\//i.test(link.url.trim())}
            onClick={async () => {
              if (await act(() => crud.create(ROUTES.attachment, { workspaceId, taskId, fileName: link.name.trim(), storageUrl: link.url.trim(), contentType: "text/uri-list" }), "Link added.")) {
                setLink({ name: "", url: "" });
                files.reload();
                onChanged();
              }
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {(defs.data ?? []).length > 0 && (
        <div className={px.block}>
          <div className={px.blockTitle}>Custom fields</div>
          {(defs.data ?? []).map((d) => {
            let options: string[] = [];
            try {
              const o = d.optionsJson ? (JSON.parse(d.optionsJson) as unknown) : [];
              options = Array.isArray(o) ? o.map((x) => (typeof x === "string" ? x : str((x as Row).label ?? (x as Row).value))) : [];
            } catch {
              options = [];
            }
            const v = shownValue(d);
            const set = (x: string) => setDraft((o) => ({ ...o, [d.id]: x }));
            return (
              <div key={d.id} className={px.addRow}>
                <Field label={`${d.name}${d.isRequired ? " *" : ""}`} className={px.grow}>
                  {d.fieldType === "SELECT" && options.length > 0 ? (
                    <Select value={v} onChange={(_, o) => set(o.value)}>
                      <option value="" />
                      {options.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </Select>
                  ) : (
                    <Input type={d.fieldType === "NUMBER" ? "number" : d.fieldType === "DATE" ? "date" : "text"} value={v} onChange={(_, o) => set(o.value)} />
                  )}
                </Field>
                <Button disabled={draft[d.id] === undefined} onClick={() => void saveValue(d)}>
                  Save
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {projectId !== null && (
        <div className={px.block}>
          <div className={px.blockTitle}>Project milestones</div>
          {(miles.data ?? []).map((m) => (
            <div key={num(m.id)} className={px.line}>
              <Checkbox
                checked={m.isReached === true}
                label={str(m.name)}
                onChange={async (_, d) => {
                  if (await act(() => crud.update(ROUTES.milestone, num(m.id), { isReached: d.checked === true, reachedAt: d.checked === true ? new Date().toISOString() : null }))) {
                    miles.reload();
                    onChanged();
                  }
                }}
              />
              <span className={styles.muted}>{m.dueDate ? fmtDate(str(m.dueDate)) : ""}</span>
              {num(m.taskId) === taskId && <Chip text="This task" />}
            </div>
          ))}
          <div className={px.addRow}>
            <Input className={px.grow} placeholder="New milestone for this task" value={ms.name} onChange={(_, d) => setMs({ ...ms, name: d.value })} />
            <Input type="date" aria-label="Milestone date" value={ms.due} onChange={(_, d) => setMs({ ...ms, due: d.value })} />
            <Button
              icon={<Add20Regular />}
              disabled={!ms.name.trim()}
              onClick={async () => {
                if (await act(() => crud.create(ROUTES.milestone, { workspaceId, projectId, taskId, name: ms.name.trim(), dueDate: ms.due || null }), "Milestone added.")) {
                  setMs({ name: "", due: "" });
                  miles.reload();
                  onChanged();
                }
              }}
            >
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ActivitySection: React.FC<{ taskId: number; ctx: PageCtx }> = ({ taskId, ctx }) => {
  const l = useLoad(async () => (await crud.list<Row>(ROUTES.activity, { taskId, sort: "-id", pageSize: 60 })).items, [taskId]);
  const nameOf = (id: unknown) => (id ? ctx.people.find((p) => p.id === num(id))?.name ?? `User ${str(id)}` : "System");
  return (
    <div className={px.section}>
      <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && (l.data ?? []).length === 0 && "No activity yet"} onRetry={l.reload} />
      {(l.data ?? []).map((a) => (
        <div key={num(a.id)} className={px.activity}>
          <span className={px.strong}>{nameOf(a.actorUserId)}</span> {nice(str(a.action))}
          {a.fieldName ? (
            <>
              {" "}
              <em>{str(a.fieldName)}</em>
              {a.oldValue || a.newValue ? `: ${str(a.oldValue) || "—"} → ${str(a.newValue) || "—"}` : ""}
            </>
          ) : null}
          <span className={styles.muted}> · {fmtStamp(str(a.createdAt))}</span>
        </div>
      ))}
    </div>
  );
};

/** The extra tabs of the task window, plus the complete / duplicate / archive buttons. */
export const TaskExtras: React.FC<{
  taskId: number;
  workspaceId: number;
  projectId: number | null;
  isCompleted: boolean;
  ctx: PageCtx;
  onChanged: () => void;
  onOpen: (id: number) => void;
  onClosed: () => void;
}> = ({ taskId, workspaceId, projectId, isCompleted, ctx, onChanged, onOpen, onClosed }) => {
  const act = useAct(ctx.notify);
  const [tab, setTab] = useState("plan");
  const [dup, setDup] = useState<{ title: string; subs: boolean; due: string } | null>(null);

  return (
    <div className={px.extras}>
      <div className={px.actions}>
        <Button
          appearance={isCompleted ? "secondary" : "primary"}
          className={isCompleted ? undefined : styles.primary}
          onClick={async () => {
            const ok = await act(() => api("POST", `/api/tasks/${taskId}/${isCompleted ? "reopen" : "complete"}`), isCompleted ? "Reopened." : "Marked as done.");
            if (ok) onChanged();
          }}
        >
          {isCompleted ? "Reopen" : "Mark done"}
        </Button>
        <Button icon={<Copy20Regular />} onClick={() => setDup({ title: "", subs: true, due: "" })}>
          Duplicate
        </Button>
        <Button
          onClick={async () => {
            if (await act(() => api("POST", "/api/tasks/bulk", { ids: [taskId], set: { ArchivedAt: new Date().toISOString() } }), "Archived.")) {
              onChanged();
              onClosed();
            }
          }}
        >
          Archive
        </Button>
      </div>
      <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))} size="small">
        <Tab value="plan">Plan</Tab>
        <Tab value="people">People & tags</Tab>
        <Tab value="more">Links & fields</Tab>
        <Tab value="activity">Activity</Tab>
      </TabList>
      {tab === "plan" && <PlanSection taskId={taskId} ctx={ctx} onChanged={onChanged} onOpen={onOpen} />}
      {tab === "people" && <PeopleSection taskId={taskId} workspaceId={workspaceId} ctx={ctx} onChanged={onChanged} />}
      {tab === "more" && <MoreSection taskId={taskId} workspaceId={workspaceId} projectId={projectId} ctx={ctx} onChanged={onChanged} />}
      {tab === "activity" && <ActivitySection taskId={taskId} ctx={ctx} />}

      <FormDialog
        open={dup !== null}
        title="Duplicate task"
        action="Duplicate"
        onClose={() => setDup(null)}
        onSubmit={async () => {
          if (!dup) return;
          const ok = await act(
            () => api("POST", `/api/tasks/${taskId}/duplicate`, { title: dup.title.trim() || null, withSubtasks: dup.subs, dueDate: dup.due || null }),
            "Duplicated.",
          );
          if (ok) {
            setDup(null);
            onChanged();
          }
        }}
      >
        <Field label="New title" hint="Leave empty to use “Copy of …”.">
          <Input value={dup?.title ?? ""} onChange={(_, d) => dup && setDup({ ...dup, title: d.value })} />
        </Field>
        <Field label="Due date">
          <Input type="date" value={dup?.due ?? ""} onChange={(_, d) => dup && setDup({ ...dup, due: d.value })} />
        </Field>
        <Checkbox label="Copy the subtasks too" checked={dup?.subs ?? true} onChange={(_, d) => dup && setDup({ ...dup, subs: d.checked === true })} />
      </FormDialog>
    </div>
  );
};

/* =================================================================== *
 *  Bulk edit
 * =================================================================== */
export const BulkDialog: React.FC<{
  ids: number[];
  open: boolean;
  columns: { id: number; label: string }[];
  ctx: PageCtx;
  onClose: () => void;
  onDone: () => void;
}> = ({ ids, open, columns, ctx, onClose, onDone }) => {
  const [v, setV] = useState({ priority: "", status: "", due: "", done: "", addA: "", remA: "", addT: "", remT: "", archive: false });
  const [busy, setBusy] = useState(false);
  const tags = useLoad(async () => (await crud.list<{ id: number; name: string }>(ROUTES.tag, { workspaceId: ctx.workspaceId, sort: "name" })).items, [ctx.workspaceId]);
  useEffect(() => {
    if (open) setV({ priority: "", status: "", due: "", done: "", addA: "", remA: "", addT: "", remT: "", archive: false });
  }, [open]);

  const set: Record<string, unknown> = {};
  if (v.priority) set.Priority = v.priority;
  if (v.status) set.StatusId = Number(v.status);
  if (v.due) set.DueDate = v.due;
  if (v.done) set.IsCompleted = v.done === "yes";
  if (v.archive) set.ArchivedAt = new Date().toISOString();
  const body = {
    ids,
    set: Object.keys(set).length ? set : undefined,
    addAssignees: v.addA ? [Number(v.addA)] : undefined,
    removeAssignees: v.remA ? [Number(v.remA)] : undefined,
    addTags: v.addT ? [Number(v.addT)] : undefined,
    removeTags: v.remT ? [Number(v.remT)] : undefined,
  };
  const anything = Object.values(body).some((x, i) => i > 0 && x !== undefined);

  const people = (value: string, onChange: (x: string) => void, label: string) => (
    <Select aria-label={label} value={value} onChange={(_, d) => onChange(d.value)}>
      <option value="">{label}</option>
      {ctx.people.map((p) => (
        <option key={p.id} value={String(p.id)}>
          {p.name}
        </option>
      ))}
    </Select>
  );
  const tagSel = (value: string, onChange: (x: string) => void, label: string) => (
    <Select aria-label={label} value={value} onChange={(_, d) => onChange(d.value)}>
      <option value="">{label}</option>
      {(tags.data ?? []).map((t) => (
        <option key={t.id} value={String(t.id)}>
          {t.name}
        </option>
      ))}
    </Select>
  );

  return (
    <FormDialog
      open={open}
      title={`Edit ${ids.length} ${ids.length === 1 ? "task" : "tasks"}`}
      action="Apply"
      busy={busy}
      disabled={!anything}
      onClose={onClose}
      onSubmit={async () => {
        setBusy(true);
        try {
          const r = await api<{ updated: number[]; failed: { id: number; code: string }[] }>("POST", "/api/tasks/bulk", body);
          if (r.failed.length) ctx.notify("error", `${r.updated.length} changed, ${r.failed.length} could not be changed (${r.failed.map((f) => f.code.replace("error.", "")).join(", ")}).`);
          else ctx.notify("success", `${r.updated.length} ${r.updated.length === 1 ? "task" : "tasks"} changed.`);
          onDone();
        } catch (e) {
          ctx.notify("error", msgOf(e));
        } finally {
          setBusy(false);
        }
      }}
    >
      <p className={styles.muted}>Only the boxes you fill in are changed.</p>
      <div className={px.grid}>
        <Field label="Priority">
          <Select value={v.priority} onChange={(_, d) => setV({ ...v, priority: d.value })}>
            <option value="">Keep as is</option>
            {["NONE", "LOW", "MEDIUM", "HIGH", "URGENT"].map((p) => (
              <option key={p} value={p}>
                {nice(p)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Column">
          <Select value={v.status} onChange={(_, d) => setV({ ...v, status: d.value })}>
            <option value="">Keep as is</option>
            {columns.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Due date">
          <Input type="date" value={v.due} onChange={(_, d) => setV({ ...v, due: d.value })} />
        </Field>
        <Field label="Done">
          <Select value={v.done} onChange={(_, d) => setV({ ...v, done: d.value })}>
            <option value="">Keep as is</option>
            <option value="yes">Mark done</option>
            <option value="no">Reopen</option>
          </Select>
        </Field>
        <Field label="Add person">{people(v.addA, (x) => setV({ ...v, addA: x }), "Choose")}</Field>
        <Field label="Remove person">{people(v.remA, (x) => setV({ ...v, remA: x }), "Choose")}</Field>
        <Field label="Add tag">{tagSel(v.addT, (x) => setV({ ...v, addT: x }), "Choose")}</Field>
        <Field label="Remove tag">{tagSel(v.remT, (x) => setV({ ...v, remT: x }), "Choose")}</Field>
      </div>
      <Checkbox label="Archive these tasks" checked={v.archive} onChange={(_, d) => setV({ ...v, archive: d.checked === true })} />
    </FormDialog>
  );
};

/* =================================================================== *
 *  Templates
 * =================================================================== */
const SAMPLE_TEMPLATE = JSON.stringify(
  { tasks: [{ title: "Kick-off meeting", offsetDays: 2, priority: "HIGH", tags: ["launch"], subtasks: [{ title: "Send the agenda", offsetDays: 1 }] }, { title: "Review results", offsetDays: 14, estimateMinutes: 60 }] },
  null,
  2,
);

export const TemplatesDialog: React.FC<{ open: boolean; ctx: PageCtx; onClose: () => void; onUsed: () => void }> = ({ open, ctx, onClose, onUsed }) => {
  const act = useAct(ctx.notify);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ name: "", description: "", json: SAMPLE_TEMPLATE });
  const [due, setDue] = useState(iso(new Date()));
  const l = useLoad(async () => (open ? (await crud.list<Row>(ROUTES.template, { workspaceId: ctx.workspaceId, sort: "name" })).items : []), [open, ctx.workspaceId]);
  const jsonOk = useMemo(() => {
    try {
      JSON.parse(f.json);
      return true;
    } catch {
      return false;
    }
  }, [f.json]);

  return (
    <Dialog open={open} onOpenChange={(_, o) => !o.open && onClose()}>
      <DialogSurface className={`${styles.dialog} ${px.wide}`}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Task templates
          </DialogTitle>
          <DialogContent className={styles.formBody}>
            <Field label="Base date" hint="Due dates in the template are counted from this day.">
              <Input type="date" value={due} onChange={(_, d) => setDue(d.value)} />
            </Field>
            <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && (l.data ?? []).length === 0 && "No templates yet"} onRetry={l.reload} />
            {(l.data ?? []).map((t) => (
              <div key={num(t.id)} className={px.line}>
                <div className={px.grow}>
                  <div className={px.strong}>{str(t.name)}</div>
                  <div className={styles.muted}>{str(t.description)}</div>
                </div>
                <Button
                  size="small"
                  className={styles.primary}
                  appearance="primary"
                  disabled={ctx.projectId === null}
                  onClick={async () => {
                    const ok = await act(async () => {
                      const r = await api<{ created: number }>("POST", `/api/tasks/templates/${num(t.id)}/instantiate`, { projectId: ctx.projectId, dueDate: due });
                      ctx.notify("success", `${r.created} ${r.created === 1 ? "task" : "tasks"} created.`);
                    });
                    if (ok) onUsed();
                  }}
                >
                  Use in this project
                </Button>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  aria-label={`Delete template ${str(t.name)}`}
                  onClick={async () => {
                    if (await act(() => crud.remove(ROUTES.template, num(t.id)))) l.reload();
                  }}
                />
              </div>
            ))}
            {ctx.projectId === null && <span className={styles.muted}>Open a project to use a template.</span>}
            {!creating ? (
              <div>
                <Button icon={<Add20Regular />} onClick={() => setCreating(true)}>
                  New template
                </Button>
              </div>
            ) : (
              <div className={px.block}>
                <Field label="Name" required>
                  <Input value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
                </Field>
                <Field label="Description">
                  <Input value={f.description} onChange={(_, d) => setF({ ...f, description: d.value })} />
                </Field>
                <Field
                  label="Tasks (JSON)"
                  validationState={jsonOk ? "none" : "error"}
                  validationMessage={jsonOk ? null : "This is not valid JSON."}
                  hint="Each task: title, offsetDays, priority, description, estimateMinutes, storyPoints, tags, subtasks."
                >
                  <Textarea value={f.json} rows={10} resize="vertical" onChange={(_, d) => setF({ ...f, json: d.value })} />
                </Field>
                <div className={px.actions}>
                  <Button
                    appearance="primary"
                    className={styles.primary}
                    disabled={!f.name.trim() || !jsonOk}
                    onClick={async () => {
                      if (await act(() => crud.create(ROUTES.template, { workspaceId: ctx.workspaceId, name: f.name.trim(), description: f.description.trim() || null, templateJson: f.json, isShared: true }), "Template saved.")) {
                        setCreating(false);
                        setF({ name: "", description: "", json: SAMPLE_TEMPLATE });
                        l.reload();
                      }
                    }}
                  >
                    Save template
                  </Button>
                  <Button onClick={() => setCreating(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Close</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* =================================================================== *
 *  Project settings: details, columns, custom fields, members
 * =================================================================== */
interface StatusRow {
  id: number;
  name: string;
  category: string;
  color?: string | null;
  sortOrder: number;
  isDefault: boolean;
  wipLimit?: number | null;
}

export const ProjectSettingsDialog: React.FC<{
  project: Project | null;
  ctx: PageCtx;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}> = ({ project, ctx, onClose, onChanged, onDeleted }) => {
  const act = useAct(ctx.notify);
  const [tab, setTab] = useState("details");
  const [d, setD] = useState({ name: "", description: "", color: "", status: "ACTIVE", health: "", start: "", end: "" });
  const pid = project?.id ?? null;
  const statuses = useLoad(async () => (pid === null ? [] : (await crud.list<StatusRow>(R.status, { projectId: pid, sort: "sortOrder" })).items), [pid]);
  const defs = useLoad(async () => (pid === null ? [] : (await crud.list<FieldDef & { sortOrder: number }>(ROUTES.fieldDef, { projectId: pid, sort: "sortOrder" })).items), [pid]);
  const members = useLoad(async () => (pid === null ? [] : (await crud.list<Row>(ROUTES.projectMember, { projectId: pid })).items), [pid]);
  const fav = useLoad(async () => (pid === null ? [] : (await crud.list<Row>(ROUTES.favorite, { projectId: pid })).items), [pid]);
  const [ns, setNs] = useState({ name: "", category: "IN_PROGRESS" });
  const [nf, setNf] = useState({ name: "", type: "TEXT", options: "", required: false });
  const [nm, setNm] = useState({ userId: "", role: "MEMBER" });

  useEffect(() => {
    if (project)
      setD({ name: project.name, description: project.description ?? "", color: project.color ?? "", status: project.status, health: project.health ?? "", start: "", end: "" });
    setTab("details");
  }, [project]);

  if (!project) return null;
  const sorted = statuses.data ?? [];
  const swap = async (a: StatusRow, b: StatusRow | undefined) => {
    if (!b) return;
    const ok = await act(async () => {
      await crud.update(R.status, a.id, { sortOrder: b.sortOrder });
      await crud.update(R.status, b.id, { sortOrder: a.sortOrder });
    });
    if (ok) {
      statuses.reload();
      onChanged();
    }
  };
  const nameOf = (id: number) => ctx.people.find((p) => p.id === id)?.name ?? `User ${id}`;
  const isFav = (fav.data ?? []).length > 0;

  return (
    <Dialog open onOpenChange={(_, o) => !o.open && onClose()}>
      <DialogSurface className={`${styles.dialog} ${px.wide}`}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Project settings · {project.name}
          </DialogTitle>
          <DialogContent className={styles.formBody}>
            <TabList selectedValue={tab} onTabSelect={(_, o) => setTab(String(o.value))} size="small">
              <Tab value="details">Details</Tab>
              <Tab value="columns">Columns</Tab>
              <Tab value="fields">Custom fields</Tab>
              <Tab value="members">Members</Tab>
            </TabList>

            {tab === "details" && (
              <>
                <Field label="Name" required>
                  <Input value={d.name} onChange={(_, o) => setD({ ...d, name: o.value })} />
                </Field>
                <Field label="Description">
                  <Textarea value={d.description} rows={2} resize="vertical" onChange={(_, o) => setD({ ...d, description: o.value })} />
                </Field>
                <div className={px.grid}>
                  <Field label="Status">
                    <Select value={d.status} onChange={(_, o) => setD({ ...d, status: o.value })}>
                      {["ACTIVE", "ON_HOLD", "COMPLETED", "CANCELLED"].map((s) => (
                        <option key={s} value={s}>
                          {nice(s)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Health">
                    <Select value={d.health} onChange={(_, o) => setD({ ...d, health: o.value })}>
                      <option value="">Not set</option>
                      {["ON_TRACK", "AT_RISK", "OFF_TRACK"].map((s) => (
                        <option key={s} value={s}>
                          {nice(s)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Colour">
                    <Input value={d.color} onChange={(_, o) => setD({ ...d, color: o.value })} placeholder="#2ea043" />
                  </Field>
                  <Field label="Start">
                    <Input type="date" value={d.start} onChange={(_, o) => setD({ ...d, start: o.value })} />
                  </Field>
                  <Field label="End">
                    <Input type="date" value={d.end} onChange={(_, o) => setD({ ...d, end: o.value })} />
                  </Field>
                </div>
                <div className={px.actions}>
                  <Button
                    appearance="primary"
                    className={styles.primary}
                    disabled={!d.name.trim()}
                    onClick={async () => {
                      const body: Record<string, unknown> = { name: d.name.trim(), description: d.description.trim() || null, status: d.status, health: d.health || null, color: d.color.trim() || null };
                      if (d.start) body.startDate = d.start;
                      if (d.end) body.endDate = d.end;
                      if (await act(() => crud.update(R.project, project.id, body), "Saved.")) onChanged();
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={async () => {
                      const ok = await act(() => (isFav ? crud.remove(ROUTES.favorite, num((fav.data ?? [])[0]?.id)) : crud.create(ROUTES.favorite, { workspaceId: project.workspaceId, projectId: project.id })));
                      if (ok) fav.reload();
                    }}
                  >
                    {isFav ? "Remove from favourites" : "Add to favourites"}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (await act(() => crud.update(R.project, project.id, { isArchived: true }), "Project archived.")) {
                        onDeleted();
                        onClose();
                      }
                    }}
                  >
                    Archive project
                  </Button>
                  <Button
                    icon={<Delete20Regular />}
                    onClick={async () => {
                      if (await act(() => crud.remove(R.project, project.id), "Project deleted.")) {
                        onDeleted();
                        onClose();
                      }
                    }}
                  >
                    Delete project
                  </Button>
                </div>
              </>
            )}

            {tab === "columns" && (
              <>
                <Wait loading={statuses.loading && !statuses.data} error={statuses.error} onRetry={statuses.reload} />
                {sorted.map((s, i) => (
                  <div key={s.id} className={px.line}>
                    <Input
                      className={px.grow}
                      defaultValue={s.name}
                      aria-label="Column name"
                      onBlur={async (e) => {
                        const v = e.currentTarget.value.trim();
                        if (v && v !== s.name && (await act(() => crud.update(R.status, s.id, { name: v })))) {
                          statuses.reload();
                          onChanged();
                        }
                      }}
                    />
                    <Select
                      aria-label="Kind"
                      value={s.category}
                      onChange={async (_, o) => {
                        if (await act(() => crud.update(R.status, s.id, { category: o.value }))) {
                          statuses.reload();
                          onChanged();
                        }
                      }}
                    >
                      <option value="TODO">To do</option>
                      <option value="IN_PROGRESS">In progress</option>
                      <option value="DONE">Done</option>
                    </Select>
                    <Input
                      type="number"
                      className={px.small}
                      aria-label="Work-in-progress limit"
                      placeholder="WIP"
                      defaultValue={s.wipLimit ? String(s.wipLimit) : ""}
                      onBlur={async (e) => {
                        const v = e.currentTarget.value.trim();
                        if ((v === "" ? null : Number(v)) !== (s.wipLimit ?? null) && (await act(() => crud.update(R.status, s.id, { wipLimit: v === "" ? null : Number(v) })))) {
                          statuses.reload();
                          onChanged();
                        }
                      }}
                    />
                    <Button size="small" appearance="subtle" icon={<ArrowUp16Regular />} aria-label="Move up" disabled={i === 0} onClick={() => void swap(s, sorted[i - 1])} />
                    <Button size="small" appearance="subtle" icon={<ArrowDown16Regular />} aria-label="Move down" disabled={i === sorted.length - 1} onClick={() => void swap(s, sorted[i + 1])} />
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<Delete20Regular />}
                      aria-label={`Delete column ${s.name}`}
                      onClick={async () => {
                        if (await act(() => crud.remove(R.status, s.id))) {
                          statuses.reload();
                          onChanged();
                        }
                      }}
                    />
                  </div>
                ))}
                <div className={px.addRow}>
                  <Input className={px.grow} placeholder="New column" value={ns.name} onChange={(_, o) => setNs({ ...ns, name: o.value })} />
                  <Select aria-label="Kind" value={ns.category} onChange={(_, o) => setNs({ ...ns, category: o.value })}>
                    <option value="TODO">To do</option>
                    <option value="IN_PROGRESS">In progress</option>
                    <option value="DONE">Done</option>
                  </Select>
                  <Button
                    icon={<Add20Regular />}
                    disabled={!ns.name.trim()}
                    onClick={async () => {
                      const next = (sorted[sorted.length - 1]?.sortOrder ?? 0) + 10;
                      if (await act(() => crud.create(R.status, { workspaceId: project.workspaceId, projectId: project.id, name: ns.name.trim(), category: ns.category, sortOrder: next, isDefault: sorted.length === 0 }))) {
                        setNs({ name: "", category: "IN_PROGRESS" });
                        statuses.reload();
                        onChanged();
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </>
            )}

            {tab === "fields" && (
              <>
                <Wait loading={defs.loading && !defs.data} error={defs.error} empty={!defs.loading && (defs.data ?? []).length === 0 && "No custom fields yet"} onRetry={defs.reload} />
                {(defs.data ?? []).map((f) => (
                  <div key={f.id} className={px.line}>
                    <span className={px.grow}>
                      <span className={px.strong}>{f.name}</span> <Chip text={f.fieldType} /> {f.isRequired ? <Chip text="Required" /> : null}
                    </span>
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<Delete20Regular />}
                      aria-label={`Delete field ${f.name}`}
                      onClick={async () => {
                        if (await act(() => crud.remove(ROUTES.fieldDef, f.id))) defs.reload();
                      }}
                    />
                  </div>
                ))}
                <div className={px.addRow}>
                  <Input className={px.grow} placeholder="Field name" value={nf.name} onChange={(_, o) => setNf({ ...nf, name: o.value })} />
                  <Select aria-label="Type" value={nf.type} onChange={(_, o) => setNf({ ...nf, type: o.value })}>
                    {["TEXT", "NUMBER", "DATE", "SELECT"].map((t) => (
                      <option key={t} value={t}>
                        {nice(t)}
                      </option>
                    ))}
                  </Select>
                  {nf.type === "SELECT" && <Input placeholder="Choices, comma separated" value={nf.options} onChange={(_, o) => setNf({ ...nf, options: o.value })} />}
                  <Checkbox label="Required" checked={nf.required} onChange={(_, o) => setNf({ ...nf, required: o.checked === true })} />
                  <Button
                    icon={<Add20Regular />}
                    disabled={!nf.name.trim() || (nf.type === "SELECT" && !nf.options.trim())}
                    onClick={async () => {
                      const options = nf.type === "SELECT" ? JSON.stringify(nf.options.split(",").map((x) => x.trim()).filter(Boolean)) : null;
                      if (await act(() => crud.create(ROUTES.fieldDef, { workspaceId: project.workspaceId, projectId: project.id, name: nf.name.trim(), fieldType: nf.type, optionsJson: options, isRequired: nf.required, sortOrder: ((defs.data ?? []).length + 1) * 10 }))) {
                        setNf({ name: "", type: "TEXT", options: "", required: false });
                        defs.reload();
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </>
            )}

            {tab === "members" && (
              <>
                <p className={styles.muted}>People who are not listed here still see the project if they belong to the workspace, unless the project is private.</p>
                <Wait loading={members.loading && !members.data} error={members.error} empty={!members.loading && (members.data ?? []).length === 0 && "No project members yet"} onRetry={members.reload} />
                {(members.data ?? []).map((m) => (
                  <div key={num(m.id)} className={px.line}>
                    <span className={px.grow}>{nameOf(num(m.userId))}</span>
                    <Select
                      aria-label="Role"
                      value={str(m.role)}
                      onChange={async (_, o) => {
                        if (await act(() => crud.update(ROUTES.projectMember, num(m.id), { role: o.value }))) members.reload();
                      }}
                    >
                      {["OWNER", "ADMIN", "MEMBER", "VIEWER"].map((r) => (
                        <option key={r} value={r}>
                          {nice(r)}
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<Delete20Regular />}
                      aria-label={`Remove ${nameOf(num(m.userId))}`}
                      onClick={async () => {
                        if (await act(() => crud.remove(ROUTES.projectMember, num(m.id)))) members.reload();
                      }}
                    />
                  </div>
                ))}
                <div className={px.addRow}>
                  <Select aria-label="Person" value={nm.userId} onChange={(_, o) => setNm({ ...nm, userId: o.value })}>
                    <option value="">Add a person</option>
                    {ctx.people
                      .filter((p) => !(members.data ?? []).some((m) => num(m.userId) === p.id))
                      .map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name}
                        </option>
                      ))}
                  </Select>
                  <Select aria-label="Role" value={nm.role} onChange={(_, o) => setNm({ ...nm, role: o.value })}>
                    {["ADMIN", "MEMBER", "VIEWER"].map((r) => (
                      <option key={r} value={r}>
                        {nice(r)}
                      </option>
                    ))}
                  </Select>
                  <Button
                    icon={<Add20Regular />}
                    disabled={!nm.userId}
                    onClick={async () => {
                      if (await act(() => crud.create(ROUTES.projectMember, { workspaceId: project.workspaceId, projectId: project.id, userId: Number(nm.userId), role: nm.role }))) {
                        setNm({ userId: "", role: "MEMBER" });
                        members.reload();
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Close</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export type { Notify };